import React from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { PreviewRow } from './PublisherBulkUploadModal';

interface PublisherPreviewTableStepProps {
  previewData: PreviewRow[];
  selectedRows: number[];
  onSelectionChange: (selectedRows: number[]) => void;
  onComplete: (selectedRows: number[]) => void;
  onBack: () => void;
  loading: boolean;
}

const PublisherPreviewTableStep: React.FC<PublisherPreviewTableStepProps> = ({
  previewData,
  selectedRows,
  onSelectionChange,
  onComplete,
  onBack,
  loading,
}) => {
  const validCount = previewData.filter(row => row.isValid).length;
  const invalidCount = previewData.length - validCount;

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allValidRows = previewData
        .filter(row => row.isValid)
        .map(row => row.rowIndex);
      onSelectionChange(allValidRows);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectRow = (rowIndex: number) => {
    const isSelected = selectedRows.includes(rowIndex);
    if (isSelected) {
      onSelectionChange(selectedRows.filter(idx => idx !== rowIndex));
    } else {
      onSelectionChange([...selectedRows, rowIndex]);
    }
  };

  const handleNext = () => {
    onComplete(selectedRows);
  };

  const allValidSelected = validCount > 0 && selectedRows.length === validCount;
  const someSelected = selectedRows.length > 0 && selectedRows.length < validCount;

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Preview:</strong> {previewData.length} total rows, {validCount} valid, {invalidCount} invalid
        </Typography>
        <Typography variant="body2">
          Select the rows you want to upload. Invalid rows cannot be selected.
        </Typography>
      </Alert>

      <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected}
                  checked={allValidSelected}
                  onChange={handleSelectAll}
                  disabled={validCount === 0}
                />
              </TableCell>
              <TableCell><strong>Row</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Publisher Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>WhatsApp</strong></TableCell>
              <TableCell><strong>Communication</strong></TableCell>
              <TableCell><strong>Active</strong></TableCell>
              <TableCell><strong>Errors</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewData.map((row) => {
              const isSelected = selectedRows.includes(row.rowIndex);
              return (
                <TableRow
                  key={row.rowIndex}
                  hover
                  selected={isSelected}
                  sx={{
                    backgroundColor: row.isValid ? 'inherit' : 'error.light',
                    opacity: row.isValid ? 1 : 0.6,
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectRow(row.rowIndex)}
                      disabled={!row.isValid}
                    />
                  </TableCell>
                  <TableCell>{row.rowIndex + 1}</TableCell>
                  <TableCell>
                    {row.isValid ? (
                      <CheckIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell>{row.data.publisherName || '-'}</TableCell>
                  <TableCell>{row.data.email || '-'}</TableCell>
                  <TableCell>{row.data.whatsapp || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.data.modeOfCommunication || 'EMAIL'}
                      size="small"
                      color={row.data.modeOfCommunication === 'WHATSAPP' ? 'success' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.data.isActive !== false ? 'Yes' : 'No'}
                      size="small"
                      color={row.data.isActive !== false ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {row.errors.length > 0 && (
                      <Typography variant="caption" color="error">
                        {row.errors.join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedRows.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select at least one valid row to continue
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {selectedRows.length} row(s) selected for upload
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onBack} disabled={loading}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || selectedRows.length === 0}
          >
            Next: Confirm Upload
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PublisherPreviewTableStep;
